// The essay workshop's steps, read off the block (KULA-REVISE-WORKSHOP-PLAN.md
// §4, corrected by §0c). Pure: what the steps are, which are built, what each
// is called, what a step records under, how the start-screen choice is
// checked, how the question-parts check is scored and what a recall screen
// asks for. The player (components/.../revise/workshop/) is a subscriber to
// this; the registry reads `workshopActivityKeys` so reporting can enumerate
// a workshop's rows the way it enumerates a paper's questions.

import type { WorkshopBlock, WorkshopEdition, WorkshopLabelKey, WorkshopRevisionPage, WorkshopCopyKey, WorkshopParagraphCount } from '../types';
import { WORKSHOP_PARAGRAPH_CHOICES, workshopPairKey } from '../types';
import { WORKSHOP_COPY_DEFAULTS, WORKSHOP_STEP_IDS } from './copy';

export type WorkshopStepId = (typeof WORKSHOP_STEP_IDS)[number];

export interface WorkshopStep {
  /** `paragraph` steps share the id and differ by `n`; `key` is unique. */
  id: WorkshopStepId;
  key: string;
  title: string;
  /** For a paragraph step, 1-based. */
  n?: number;
  /** False for a step whose screens are not in this build; it shows a stub. */
  built: boolean;
}

/** The steps that play in this build. Grows PR by PR (§0c build order). */
export const WORKSHOP_BUILT_STEPS: readonly WorkshopStepId[] = ['question', 'revise', 'match', 'argue', 'missed', 'paragraphs', 'intro', 'paragraph', 'conclusion', 'essay'];

export const hasChoice = (w: WorkshopBlock): boolean => !('fixed' in w.selection);

/** How many case studies a learner ends up with. */
export const pickCount = (w: WorkshopBlock): 1 | 2 => ('fixed' in w.selection ? (w.selection.fixed.length === 2 ? 2 : 1) : w.selection.pick);

/** "two wars" or "war". */
export function unitPhrase(w: WorkshopBlock): string {
  return pickCount(w) === 2 ? `two ${w.unit.plural}` : w.unit.singular;
}

/** The step list for one workshop; `paragraphs` is how many body paragraphs the learner chose (default four until they do). */
export function workshopSteps(w: WorkshopBlock, paragraphs: WorkshopParagraphCount = 4): WorkshopStep[] {
  const titles: Record<Exclude<WorkshopStepId, 'paragraph'>, string> = {
    question: 'Understand the question',
    revise: 'Revise and recall',
    match: pickCount(w) === 2 ? `Match ${w.themeWord.singular} and evidence` : `Match ${w.themeWord.singular} and effect`,
    argue: 'Decide your argument',
    missed: 'Recall what you missed',
    paragraphs: 'Your paragraphs',
    intro: 'Write your introduction',
    conclusion: 'Write your conclusion',
    essay: 'Your essay',
  };
  const out: WorkshopStep[] = [];
  for (const id of WORKSHOP_STEP_IDS) {
    if (id === 'paragraph') {
      for (let n = 1; n <= paragraphs; n++) out.push({ id, key: `p${n}`, title: `Paragraph ${n}`, n, built: WORKSHOP_BUILT_STEPS.includes(id) });
    } else out.push({ id, key: id, title: titles[id], built: WORKSHOP_BUILT_STEPS.includes(id) });
  }
  return out;
}

// ---------------------------------------------------------------------------
// The start-screen choice.
// ---------------------------------------------------------------------------

/** Why two case studies cannot be chosen together, or null when they can. */
export function refusal(w: WorkshopBlock, a: string, b: string): string | null {
  if ('fixed' in w.selection || w.selection.pick !== 2 || !w.selection.regionRule) return null;
  const ra = w.caseStudies[a]?.region;
  const rb = w.caseStudies[b]?.region;
  if (!ra || !rb || ra !== rb) return null;
  return fillCopy(WORKSHOP_COPY_DEFAULTS.regionRefused, { units: w.unit.plural, region: ra });
}

/** Is this set of case studies one the workshop allows? */
export function selectionAllowed(w: WorkshopBlock, chosen: string[]): boolean {
  if (chosen.some((k) => !w.editions[k])) return false;
  if (new Set(chosen).size !== chosen.length) return false;
  if ('fixed' in w.selection) return chosen.length === w.selection.fixed.length && w.selection.fixed.every((k) => chosen.includes(k));
  if (chosen.length !== w.selection.pick) return false;
  return chosen.length < 2 || refusal(w, chosen[0], chosen[1]) === null;
}

/** The key the learner's choice records and resumes under: the case-study ids, sorted and joined. */
export const selectionKey = (chosen: string[]): string => (chosen.length === 2 ? workshopPairKey(chosen[0], chosen[1]) : chosen.join('+'));

// ---------------------------------------------------------------------------
// What a workshop records under. Every key is stable so a reload, a second
// device and a future teacher view read the same rows.
// ---------------------------------------------------------------------------

export const WORKSHOP_KEYS = {
  question: 'ws:question',
  choose: 'ws:choose',
  revise: (selection: string) => `ws:revise:${selection}`,
  recall: (caseStudy: string, page: number) => `ws:recall:${caseStudy}:${page}`,
  match: (caseStudy: string) => `ws:match:${caseStudy}`,
  compare: (selection: string) => `ws:compare:${selection}`,
  rank: (selection: string) => `ws:rank:${selection}`,
  position: (selection: string) => `ws:position:${selection}`,
  missed: (selection: string) => `ws:missed:${selection}`,
  plan: (selection: string, theme: number) => `ws:plan:${selection}:${theme}`,
  draft: (selection: string, part: string) => `ws:draft:${selection}:${part}`,
} as const;

/** Every key this workshop can write, for the registry's enumeration. */
export function workshopActivityKeys(w: WorkshopBlock): string[] {
  const out: string[] = [WORKSHOP_KEYS.question];
  if (hasChoice(w)) out.push(WORKSHOP_KEYS.choose);
  for (const sel of reachable(w)) {
    const key = selectionKey(sel);
    out.push(WORKSHOP_KEYS.revise(key));
    if (sel.length === 2) out.push(WORKSHOP_KEYS.compare(key));
    out.push(WORKSHOP_KEYS.rank(key), WORKSHOP_KEYS.position(key), WORKSHOP_KEYS.missed(key));
    const themes = w.editions[sel[0]]?.themes.length ?? 0;
    for (let t = 0; t < themes; t++) out.push(WORKSHOP_KEYS.plan(key, t), WORKSHOP_KEYS.draft(key, `t${t}`));
    out.push(WORKSHOP_KEYS.draft(key, 'intro'), WORKSHOP_KEYS.draft(key, 'conclusion'));
  }
  for (const [k, e] of Object.entries(w.editions)) {
    e.revision.forEach((p, i) => {
      if (p.recall) out.push(WORKSHOP_KEYS.recall(k, i));
    });
    out.push(WORKSHOP_KEYS.match(k));
  }
  return out;
}

/** Every set of case studies a learner can end up with. */
export function reachable(w: WorkshopBlock): string[][] {
  const ids = Object.keys(w.caseStudies).filter((k) => w.editions[k]);
  if ('fixed' in w.selection) return [w.selection.fixed];
  if (w.selection.pick === 1) return ids.map((k) => [k]);
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i++) for (let j = i + 1; j < ids.length; j++) if (refusal(w, ids[i], ids[j]) === null) out.push([ids[i], ids[j]]);
  return out;
}

// ---------------------------------------------------------------------------
// Step 1: the question-parts check.
// ---------------------------------------------------------------------------

export interface LabelCheck {
  correct: number;
  total: number;
  ok: boolean;
  /** The parts labelled wrongly or not at all, with the label they need. */
  wrong: Array<{ index: number; text: string; label: WorkshopLabelKey; labelName: string }>;
}

export function checkLabels(w: WorkshopBlock, tags: Record<number, WorkshopLabelKey | undefined>): LabelCheck {
  const names = Object.fromEntries(w.labels.map((l) => [l.key, l.name])) as Record<WorkshopLabelKey, string>;
  const wrong: LabelCheck['wrong'] = [];
  w.parts.forEach((p, i) => {
    if (tags[i] !== p.label) wrong.push({ index: i, text: p.text.replace(/[.,]$/, ''), label: p.label, labelName: names[p.label] ?? p.label });
  });
  return { correct: w.parts.length - wrong.length, total: w.parts.length, ok: wrong.length === 0, wrong };
}

/** "One label is used twice." or its plural, or nothing when every label is used once. */
export function labelReuseNote(w: WorkshopBlock): string {
  const counts: Partial<Record<WorkshopLabelKey, number>> = {};
  for (const p of w.parts) counts[p.label] = (counts[p.label] ?? 0) + 1;
  const reused = Object.values(counts).filter((n) => (n ?? 0) > 1);
  if (reused.length === 0) return '';
  if (reused.length === 1 && reused[0] === 2) return 'One label is used twice.';
  return 'Some labels are used more than once.';
}

// ---------------------------------------------------------------------------
// Step 3: revision pages and recall screens.
// ---------------------------------------------------------------------------

export type RevisionPart = { kind: 'page'; caseStudy: string; page: number } | { kind: 'recall'; caseStudy: string; page: number };

/**
 * The parts of the revise step for the chosen case studies, in order: each
 * case study's factor pages (each followed by its recall screen), then every
 * case study's perspective pages (those without recall), so the historians
 * come after the facts, as the shipped files order them.
 */
export function revisionParts(w: WorkshopBlock, chosen: string[]): RevisionPart[] {
  const out: RevisionPart[] = [];
  const perspectives: RevisionPart[] = [];
  for (const k of chosen) {
    const e = w.editions[k];
    if (!e) continue;
    e.revision.forEach((p, i) => {
      if (p.recall) {
        out.push({ kind: 'page', caseStudy: k, page: i });
        out.push({ kind: 'recall', caseStudy: k, page: i });
      } else perspectives.push({ kind: 'page', caseStudy: k, page: i });
    });
  }
  return [...out, ...perspectives];
}

/** The key facts a recall screen checks against, with their flashcard questions. */
export function recallKeyFacts(e: WorkshopEdition, page: WorkshopRevisionPage): Array<{ text: string; cue: string }> {
  if (!page.recall) return [];
  if (page.recall.keys?.length) return page.recall.keys;
  return (page.recall.facts ?? []).map((id) => e.facts[id]).filter((f): f is NonNullable<typeof f> => !!f).map((f) => ({ text: f.text, cue: f.cue ?? '' }));
}

/** Split a learner's recall text into facts: one per line, bullets and numbering stripped. */
export function recallLines(text: string): string[] {
  return String(text ?? '')
    .split(/\n+/)
    .map((x) => x.replace(/^[\s\-•*\d.)]+/, '').trim())
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Copy with placeholders.
// ---------------------------------------------------------------------------

export function fillCopy(template: string, vars: Record<string, string | number | undefined>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (vars[k] === undefined ? m : String(vars[k])));
}

/** An edition's line for a copy key, or the engine default, with placeholders filled. */
export function copyFor(e: WorkshopEdition | undefined, key: WorkshopCopyKey, vars: Record<string, string | number | undefined> = {}): string {
  return fillCopy(e?.copy?.[key] ?? WORKSHOP_COPY_DEFAULTS[key], vars);
}

/** The placeholder values a set of chosen case studies shares: their names and the nouns. */
export function copyVars(w: WorkshopBlock, chosen: string[], paragraphs?: number): Record<string, string> {
  const [a, b] = chosen;
  const e = a ? w.editions[a] : undefined;
  return {
    a: a ? w.caseStudies[a]?.name ?? a : '',
    b: b ? w.caseStudies[b]?.name ?? b : '',
    unit: w.unit.singular,
    units: w.unit.plural,
    theme: w.themeWord.singular,
    themes: w.themeWord.plural,
    n: e ? numberWord(e.themes.length) : '',
    themeCount: e ? String(e.themes.length) : '',
    anchor: e?.anchorTheme !== undefined ? e.themes[e.anchorTheme]?.low ?? '' : '',
    paragraphs: paragraphs ? numberWord(paragraphs) : '',
  };
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
export const numberWord = (n: number): string => WORDS[n] ?? String(n);

/** "the First World War (Europe) and the Pacific War (Asia and Oceania)" */
export function caseStudyList(w: WorkshopBlock, chosen: string[]): string {
  const names = chosen.map((k) => {
    const c = w.caseStudies[k];
    if (!c) return k;
    return c.region ? `${c.name} (${c.region})` : c.name;
  });
  if (names.length < 2) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export const capitalise = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);

export const isParagraphCount = (v: unknown): v is WorkshopParagraphCount => (WORKSHOP_PARAGRAPH_CHOICES as readonly number[]).includes(v as number);
